import { useState } from "react";
import UiIcon from "../UiIcon";
import { contributionCopy } from "./contributionCopy";
import { buildWorkflow } from "./contributionWorkflow";
import ContributionTimeline, { StatusPill, formatDateTime } from "./ContributionTimeline";
import "../../styles/cvi-contributions.css";

const FEEDBACK_TONE = { revision_requested: "warning", rejected: "danger", approved: "success" };

export function FeedbackCard({ feedback, c, language }) {
    if (!feedback) return <section className="cvi-contrib-card cvi-contrib-feedback is-empty" aria-label={c.detail.feedback.title}>
        <header><UiIcon name="send" size={15} /><strong>{c.detail.feedback.title}</strong></header>
        <p className="cvi-contrib-muted">{c.detail.feedback.none}</p>
    </section>;
    const label = { revision_requested: c.detail.feedback.revision, approved: c.detail.feedback.approved, rejected: c.detail.feedback.rejected }[feedback.kind];
    return <section className={`cvi-contrib-card cvi-contrib-feedback is-${FEEDBACK_TONE[feedback.kind] || "neutral"}`} aria-label={c.detail.feedback.title}>
        <header><UiIcon name={feedback.kind === "approved" ? "check" : "warning"} size={15} /><strong>{c.detail.feedback.title}</strong>
            {label && <span className="cvi-contrib-feedback-kind">{label}</span>}</header>
        <p>{feedback.note}</p>
        <footer>{[feedback.actorName ? `${feedback.actorName}${feedback.isYou ? ` (${c.common.you})` : ""}` : c.timeline.byUnknown, formatDateTime(feedback.at, language)].filter(Boolean).join(" · ")}</footer>
    </section>;
}

export default function ContributionDetail({
    activity, activityGroups = [], impactLevels = [], language = "en", periodLabel = null,
    ownerName = null, reviewerName = null, reviewerId = null, currentUserId = null, isOwner = false, attachments = null,
    onBack, onEdit, onSubmitDraft, onRevise, orgLabel = text => text,
}) {
    const c = contributionCopy(language);
    const d = c.detail;
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const workflow = buildWorkflow(activity, { ownerName, reviewerName, reviewerId, currentUserId });
    const group = activityGroups.find(g => g.id === activity.groupId);
    const impact = impactLevels.find(level => level.id === activity.impactLevel) || null;
    const approved = workflow.status === "approved";
    const impactInfo = approved && impact ? { label: orgLabel(impact.label), points: impact.score ?? activity.score } : null;

    const submitted = workflow.firstSubmission;
    const lastReview = workflow.lastReview;
    const awaitingReview = workflow.pending?.kind === "review";
    const reviewer = awaitingReview ? reviewerName : lastReview ? (lastReview.actorName || c.timeline.byUnknown) : reviewerName;
    const facts = [
        { label: d.owner, value: ownerName },
        { label: d.submittedOn, value: submitted ? formatDateTime(submitted.at, language) || c.common.notRecorded : d.notSubmitted },
        { label: d.reviewer, value: reviewer },
        { label: d.decidedOn, value: lastReview ? `${c.status[lastReview.kind] || ""} · ${formatDateTime(lastReview.at, language) || c.common.notRecorded}` : awaitingReview ? d.awaiting : null },
        ...(approved ? [{ label: d.impact, value: impact?.label ? orgLabel(impact.label) : null }, { label: d.score, value: activity.score != null ? String(activity.score) : null }] : []),
        { label: d.period, value: periodLabel },
    ];

    const submitDraft = async () => {
        if (busy) return;
        setBusy(true); setError("");
        try { await onSubmitDraft(); } catch (err) { setError(err?.message || c.form.errors.generic); } finally { setBusy(false); }
    };
    const actions = isOwner && <div className="cvi-contrib-head-actions">
        {workflow.status === "draft" && onEdit && <button type="button" className="adm-btn" onClick={onEdit}><UiIcon name="edit" size={15} />{d.actions.edit}</button>}
        {workflow.status === "draft" && onSubmitDraft && <button type="button" className="adm-btn adm-btn--primary" disabled={busy} aria-busy={busy} onClick={submitDraft}>
            {busy ? <span className="cvi-contrib-spinner" aria-hidden="true" /> : <UiIcon name="send" size={15} />}{busy ? d.actions.submitting : d.actions.submit}</button>}
        {workflow.status === "revision_requested" && onRevise && <button type="button" className="adm-btn adm-btn--primary" onClick={onRevise}><UiIcon name="edit" size={15} />{d.actions.revise}</button>}
    </div>;

    const challenge = activity.blockerStatus && activity.blockerStatus !== "none" ? activity : null;
    const challengeType = challenge?.blockerCategory ? c.form.challenge.types[challenge.blockerCategory] || challenge.blockerCategory : null;

    return <div className="adm-page cvi-contrib" data-cvi-i18n="managed" lang={language}>
        <nav className="cvi-contrib-crumbs"><button type="button" className="adm-btn adm-btn--sm adm-btn--ghost" onClick={onBack}><UiIcon name="chevronLeft" size={14} />{c.common.back}</button></nav>
        <header className="cvi-contrib-detail-head">
            <div className="cvi-contrib-detail-title">
                <span className="cvi-contrib-eyebrow">{[group?.label, activity.sub].filter(Boolean).map(orgLabel).join(" · ") || d.title}</span>
                <h1 className="adm-title">{activity.title}</h1>
                <div className="cvi-contrib-detail-status"><StatusPill status={workflow.status} c={c} /><span>{c.statusHelp[workflow.status]}</span></div>
            </div>
            {actions}
        </header>
        {error && <div className="adm-callout is-danger" role="alert"><UiIcon name="warning" size={15} /><div>{error}</div></div>}

        <dl className="cvi-contrib-facts">{facts.map(fact => <div key={fact.label}><dt>{fact.label}</dt><dd className={fact.value ? "" : "is-empty"}>{fact.value || "—"}</dd></div>)}</dl>

        <div className="cvi-contrib-detail-grid">
            <div className="cvi-contrib-main">
                <section className="cvi-contrib-card">
                    <header className="cvi-contrib-card-head"><div><h2>{d.description}</h2></div></header>
                    {activity.desc ? <p className="cvi-contrib-text">{activity.desc}</p> : <p className="cvi-contrib-muted">{d.noDescription}</p>}
                </section>
                <section className="cvi-contrib-card">
                    <header className="cvi-contrib-card-head"><div><h2>{d.evidence}</h2></div></header>
                    {attachments || <p className="cvi-contrib-muted">{d.noEvidence}</p>}
                </section>
                {challenge && <section className="cvi-contrib-card cvi-contrib-challenge">
                    <header className="cvi-contrib-card-head"><div><h2>{d.challenge}</h2></div>
                        <span className={`cvi-contrib-chip is-${challenge.blockerStatus}`}>{c.form.challenge.statuses[challenge.blockerStatus] || challenge.blockerStatus}</span></header>
                    <dl className="cvi-contrib-pairs">
                        {challengeType && <div><dt>{c.form.challenge.type}</dt><dd>{challengeType}</dd></div>}
                        {challenge.blockerText && <div><dt>{c.form.challenge.description}</dt><dd>{challenge.blockerText}</dd></div>}
                        {challenge.supportNeed && <div><dt>{c.form.challenge.support}</dt><dd>{challenge.supportNeed}</dd></div>}
                        {challenge.suggestedAction && <div><dt>{c.form.challenge.action}</dt><dd>{challenge.suggestedAction}</dd></div>}
                    </dl>
                </section>}
            </div>
            <div className="cvi-contrib-side">
                {(workflow.feedback || workflow.status !== "draft") && <FeedbackCard feedback={workflow.feedback} c={c} language={language} />}
                <ContributionTimeline workflow={workflow} c={c} language={language} impact={impactInfo} />
            </div>
        </div>
    </div>;
}
